import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { SectionHeader } from '@/components/ui/section-header';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  ChatBubbleLeftRightIcon,
  CreditCardIcon,
  TicketIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import { EditorFieldGroup } from '../../../components/workspaces/EntityWorkspacePrimitives';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { type ContactSearchState } from './form-sections/ContactInformation';
import {
  EventInformation,
  CustomInputsInformation_Event,
  ContactInformation,
  PaymentInformation,
} from './form-sections';

import type {
  EventLayoutProps,
  FormStep,
  Contact,
  SelectedTicket,
  CustomInput,
  EventData,
  IframeState,
  FinixForm,
} from '../drawers/types/EventPaymentTypes';
import type { Merchant } from '../../types/common';
import { calculateDashboardTicketRowSubtotal } from '../../utils/eventTicketPurchase';
import {
  formatOfflinePaymentMethod,
  isOfflinePaymentRecord,
} from '../../utils/offlinePaymentMethods';

// Extend Window interface for PaymentForm
declare global {
  interface Window {
    PaymentForm: {
      card: (callback: (state: unknown, binInformation: unknown) => void) => FinixForm;
    };
  }
}

// Helper function to get current time in the merchant's timezone (or local timezone as fallback)
const getCurrentTimeInTimezone = (merchant: Merchant | null): string => {
  const timezone = merchant?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  return new Date().toLocaleTimeString('en-US', {
    timeZone: timezone,
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
  });
};

// Helper function to get the effective timezone
const getEffectiveTimezone = (merchant: Merchant | null): string => {
  return merchant?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
};

// Navigation buttons for form steps
interface StepNavigationProps {
  currentStep: FormStep;
  onBack: () => void;
  onContinue: () => void;
  paymentType?: string;
  isEditMode?: boolean;
  selectedContact?: Contact | null;
  isNewContact?: boolean;
}

const StepNavigation: React.FC<StepNavigationProps> = ({ currentStep, onBack, onContinue }) => {
  // Determine if Continue button should be shown
  // Show Continue on all steps except payment_information (last step)
  const shouldShowContinue = currentStep !== 'payment_information';

  return (
    <div className="mt-4 flex w-full justify-between">
      {/* Back Button */}
      <div>
        {currentStep !== 'form_information' && (
          <Button onClick={onBack} className="h-[28px] gap-x-1 px-3 text-sm text-ink-muted">
            <ArrowLeftIcon className="h-[14px] w-[14px] text-ink-muted" />
            <div>Back</div>
          </Button>
        )}
      </div>

      {/* Continue Button */}
      <div>
        {shouldShowContinue && (
          <Button onClick={onContinue} className="h-[28px] gap-x-1 px-3 text-sm text-ink-muted">
            <div>Continue</div>
            <ArrowRightIcon className="h-[14px] w-[14px] text-ink-muted" />
          </Button>
        )}
      </div>
    </div>
  );
};

interface PaymentEditorSectionProps {
  presentation?: 'drawer' | 'workspace';
  title: string;
  description: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  hasErrors: boolean;
  active: boolean;
  onClick: () => void;
  keepMounted?: boolean;
  children: React.ReactNode;
}

const PaymentEditorSection = ({
  presentation,
  title,
  description,
  icon,
  hasErrors,
  active,
  onClick,
  keepMounted = false,
  children,
}: PaymentEditorSectionProps) => {
  if (presentation === 'workspace') {
    return (
      <EditorFieldGroup
        title={title}
        description={description}
        icon={icon}
        hasErrors={hasErrors}
        enabled
      >
        <div className="[&_[data-field-surface]]:!bg-white [&_input:not([type=radio]):not([type=checkbox])]:!bg-white [&_select]:!bg-white [&_textarea]:!bg-white">
          {children}
        </div>
      </EditorFieldGroup>
    );
  }

  const content = keepMounted ? (
    <div className={active ? '' : 'hidden'}>{children}</div>
  ) : active ? (
    children
  ) : null;

  return (
    <>
      <SectionHeader title={title} hasErrors={hasErrors} onClick={onClick} />
      {content}
    </>
  );
};

export default function EventLayout(props: EventLayoutProps): React.ReactElement {
  const {
    presentation,
    formData,
    merchant,
    setFormData,
    handleFormDataChange,
    paymentError,
    invalidFields,
    setInvalidFields,
    handleFieldFocus,
    stripeCardRef,
    finixCardRef,
    finixBankRef,
    form,
    setForm,
    setTokenID,
    isDrawerOpen,
    currentStep,
    setCurrentStep,
    handleBack,
    handleContinue,
    isEditMode,
    transaction,
    validatePromoCode,
    removePromoCode,
    isPromoProcessing,
    promoMessage,
    selectedContact,
    setSelectedContact,
    isNewContact,
    setIsNewContact,
  } = props;
  const { paymentType } = formData || {};
  const [possibleForms, setPossibleForms] = useState<EventData[]>([]);

  // Event ticket selection state (ticket-centric approach)
  const [selectedTickets, setSelectedTickets] = useState<SelectedTicket[]>([]);

  // Contact search state - lifted from ContactInformation for state persistence across step navigation
  const [contactSearchTerm, setContactSearchTerm] = useState<string>('');
  const [contactSearchResults, setContactSearchResults] = useState<Contact[]>([]);
  const [contactHasSearched, setContactHasSearched] = useState<boolean>(false);
  const [contactSelectedEmailIndex, setContactSelectedEmailIndex] = useState<number>(0);
  const [contactSelectedPhoneIndex, setContactSelectedPhoneIndex] = useState<number>(0);
  const [contactSelectedAddressIndex, setContactSelectedAddressIndex] = useState<number>(0);
  const [contactIsAddingNewEmail, setContactIsAddingNewEmail] = useState<boolean>(false);
  const [contactIsAddingNewPhone, setContactIsAddingNewPhone] = useState<boolean>(false);
  const [contactIsAddingNewAddress, setContactIsAddingNewAddress] = useState<boolean>(false);

  // Create the contactSearchState object to pass to ContactInformation
  const contactSearchState: ContactSearchState = {
    searchTerm: contactSearchTerm,
    setSearchTerm: setContactSearchTerm,
    contacts: contactSearchResults,
    setContacts: setContactSearchResults,
    hasSearched: contactHasSearched,
    setHasSearched: setContactHasSearched,
    selectedEmailIndex: contactSelectedEmailIndex,
    setSelectedEmailIndex: setContactSelectedEmailIndex,
    selectedPhoneIndex: contactSelectedPhoneIndex,
    setSelectedPhoneIndex: setContactSelectedPhoneIndex,
    selectedAddressIndex: contactSelectedAddressIndex,
    setSelectedAddressIndex: setContactSelectedAddressIndex,
    isAddingNewEmail: contactIsAddingNewEmail,
    setIsAddingNewEmail: setContactIsAddingNewEmail,
    isAddingNewPhone: contactIsAddingNewPhone,
    setIsAddingNewPhone: setContactIsAddingNewPhone,
    isAddingNewAddress: contactIsAddingNewAddress,
    setIsAddingNewAddress: setContactIsAddingNewAddress,
  };

  // ============================================================================
  // Payment Information State - lifted from PaymentInformation for state control
  // ============================================================================

  // Stripe initialization state
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);

  // Error handling state
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [showErrorAlert, setShowErrorAlert] = useState<boolean>(false);
  const [sendingData, setSendingData] = useState<boolean>(false);

  // Finix iframe state
  const [iframeStates, setIframeStates] = useState<IframeState | null>(null);

  // Bank account state
  const [accountNumber, setAccountNumber] = useState<string>(formData.accountNumber || '');
  const [routingNumber, setRoutingNumber] = useState<string>(formData.routingNumber || '');
  const [accountType, setAccountType] = useState<'CHECKING' | 'SAVINGS'>(
    formData.accountType === 'CHECKING' || formData.accountType === 'SAVINGS'
      ? formData.accountType
      : 'CHECKING',
  );

  // Offline payment fee state
  const [offlineFeeCovered, setOfflineFeeCovered] = useState<boolean>(formData.feeCovered || false);
  const [offlineCustomFeeAmount, setOfflineCustomFeeAmount] = useState<string>(
    formData.customFeeAmount ? String(formData.customFeeAmount) : '',
  );

  // Payment date and time state (for offline payments)
  const [paymentDate, setPaymentDate] = useState<Date>(
    formData.timestamp ? new Date(formData.timestamp) : new Date(),
  );
  const [paymentTime, setPaymentTime] = useState<string>(getCurrentTimeInTimezone(merchant));

  // Track if this is the initial mount to prevent resetting state on navigation
  const isInitialMount = useRef(true);
  const prevPaymentType = useRef(paymentType);

  // Payment-related derived values and setters for PaymentInformation component
  const feeCovered = formData.feeCovered || false;
  const setFeeCovered = useCallback(
    (value: boolean | ((prev: boolean) => boolean)) => {
      const newValue = typeof value === 'function' ? value(formData.feeCovered || false) : value;
      handleFormDataChange({ feeCovered: newValue });
    },
    [formData.feeCovered, handleFormDataChange],
  );

  const paymentMethod = formData.paymentMethod || 'Card';
  const setPaymentMethod = useCallback(
    (value: string | ((prev: string) => string)) => {
      const newValue =
        typeof value === 'function' ? value(formData.paymentMethod || 'Card') : value;
      handleFormDataChange({ paymentMethod: newValue as 'Card' | 'Bank' });
    },
    [formData.paymentMethod, handleFormDataChange],
  );

  const setPaymentTypeCallback = useCallback(
    (value: string | ((prev: string) => string)) => {
      const newValue = typeof value === 'function' ? value(paymentType || 'Online') : value;
      handleFormDataChange({ paymentType: newValue });
    },
    [paymentType, handleFormDataChange],
  );

  // ============================================================================
  // Payment Information Effects
  // ============================================================================

  // Initialize Stripe when merchant has Stripe account
  useEffect(() => {
    if (merchant && merchant.stripeAccountID) {
      setStripePromise(
        loadStripe(import.meta.env.VITE_APP_STRIPE_PK, {
          stripeAccount: merchant.stripeAccountID,
        }),
      );
    }
  }, [merchant]);

  // Initialize the Finix payment form when drawer opens
  useEffect(() => {
    if (!isDrawerOpen || form) return;

    const initializeForm = () => {
      try {
        const newForm = window.PaymentForm.card((state) => {
          if (state !== undefined) {
            setIframeStates(state as IframeState);
          }
        });
        setForm(newForm);
      } catch (error) {
        console.error('Error initializing payment form:', error);
        setErrorMessage('Failed to initialize payment form');
        setShowErrorAlert(true);
      }
    };

    initializeForm();
  }, [isDrawerOpen, form, setForm]);

  // Sync bank account state to formData
  useEffect(() => {
    setFormData((prevData) => ({
      ...prevData,
      accountNumber: accountNumber,
      routingNumber: routingNumber,
      accountType: accountType,
    }));
  }, [accountNumber, routingNumber, accountType]);

  // Update paymentTime when merchant timezone becomes available
  useEffect(() => {
    if (!isEditMode && isDrawerOpen && merchant?.timezone) {
      setPaymentTime(getCurrentTimeInTimezone(merchant));
    }
  }, [merchant?.timezone, isDrawerOpen, isEditMode]);

  // Reset offline fee state when payment type actually changes (not on initial mount)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      prevPaymentType.current = paymentType;
      return;
    }

    if (prevPaymentType.current === paymentType) {
      return;
    }
    prevPaymentType.current = paymentType;

    if (isEditMode) return;

    // Reset offline-specific state
    setOfflineFeeCovered(false);
    setOfflineCustomFeeAmount('');

    // Set feeCovered based on payment type: true for Online, false for offline
    const isOnline = paymentType === 'Online';
    setFormData((prevData) => ({
      ...prevData,
      feeCovered: isOnline,
      customFeeAmount: '',
    }));
  }, [paymentType, isEditMode]);

  // Populate state from transaction in edit mode
  useEffect(() => {
    if (isEditMode && transaction) {
      const isOfflinePayment = isOfflinePaymentRecord(transaction);

      if (transaction?.importedTransaction) {
        setPaymentTypeCallback(
          transaction?.paymentMethod ? formatOfflinePaymentMethod(transaction.paymentMethod) : '',
        );
        setOfflineFeeCovered(transaction?.coverFees === 'Yes' || transaction?.coverFees === true);
        setOfflineCustomFeeAmount(
          transaction?.coverFees === 'Yes' || transaction?.coverFees === true
            ? String(transaction?.fees ?? '')
            : '',
        );
        if (transaction?.timestamp) {
          const timestamp = parseTimestamp(transaction.timestamp);
          setPaymentDate(timestamp);
          setPaymentTime(extractTimeFromTimestamp(timestamp, merchant));
        }
      } else if (isOfflinePayment) {
        const paymentTypeFromMethod = transaction.paymentMethod
          ? formatOfflinePaymentMethod(transaction.paymentMethod)
          : 'Other';
        setPaymentTypeCallback(paymentTypeFromMethod);
        setOfflineFeeCovered(transaction?.coverFees === 'Yes' || transaction?.coverFees === true);
        setOfflineCustomFeeAmount(
          transaction?.coverFees === 'Yes' || transaction?.coverFees === true
            ? String(transaction?.fees ?? '')
            : '',
        );
        if (transaction?.timestamp) {
          const timestamp = parseTimestamp(transaction.timestamp);
          setPaymentDate(timestamp);
          setPaymentTime(extractTimeFromTimestamp(timestamp, merchant));
        }
      } else {
        setPaymentTypeCallback('Online');
      }
    }
  }, [isEditMode, transaction, merchant]);

  // Reset payment state when drawer closes
  useEffect(() => {
    if (!isDrawerOpen) {
      setPaymentTypeCallback('Online');
      setOfflineFeeCovered(false);
      setOfflineCustomFeeAmount('');
      setPaymentDate(new Date());
      setPaymentTime(getCurrentTimeInTimezone(merchant));
      setAccountNumber('');
      setRoutingNumber('');
      setAccountType('CHECKING');
      setErrorMessage('');
      setShowErrorAlert(false);
      setSendingData(false);
      setIframeStates(null);
      // Reset the initial mount tracker
      isInitialMount.current = true;
    }
  }, [isDrawerOpen, merchant]);

  // ============================================================================
  // Payment Information Helper Functions
  // ============================================================================

  // Helper function to parse timestamp
  const parseTimestamp = (timestampStr: string): Date => {
    let str = timestampStr;
    if (!str.includes('Z') && !str.includes('+') && !str.match(/\d{2}:\d{2}:\d{2}\.\d+-/)) {
      str = str.replace(' ', 'T');
      if (!str.endsWith('Z')) {
        str += 'Z';
      }
    }
    return new Date(str);
  };

  // Helper function to extract time from timestamp
  const extractTimeFromTimestamp = (timestamp: Date, merchantData: Merchant | null): string => {
    const timezone = getEffectiveTimezone(merchantData);
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    const timeParts = formatter.formatToParts(timestamp);
    const hours = timeParts.find((p) => p.type === 'hour')?.value || '00';
    const minutes = timeParts.find((p) => p.type === 'minute')?.value || '00';
    return `${hours}:${minutes}`;
  };

  // Helper function to combine date and time in the merchant's timezone
  const applyPaymentTimeToDate = (date: Date | null, time: string | null): string | Date | null => {
    if (!date || !time) return date;

    try {
      const [hours, minutes] = time.split(':').map(Number);
      const timezone = getEffectiveTimezone(merchant);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const dateTimeString = `${year}-${month}-${day}T${String(hours).padStart(2, '0')}:${String(
        minutes,
      ).padStart(2, '0')}:00`;

      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        timeZoneName: 'shortOffset',
      });

      const testDate = new Date(dateTimeString);
      const parts = formatter.formatToParts(testDate);
      const offsetPart = parts.find((p) => p.type === 'timeZoneName');

      if (offsetPart) {
        const offsetMatch = offsetPart.value.match(/GMT([+-])(\d{1,2})(?::(\d{2}))?/);
        if (offsetMatch) {
          const sign = offsetMatch[1];
          const offsetHours = offsetMatch[2].padStart(2, '0');
          const offsetMinutes = offsetMatch[3] || '00';
          const isoString = `${dateTimeString}${sign}${offsetHours}:${offsetMinutes}`;
          return new Date(isoString).toISOString();
        }
      }

      // Fallback
      const targetDate = new Date(date);
      targetDate.setHours(hours, minutes, 0, 0);
      return targetDate.toISOString();
    } catch (error) {
      console.error('Error applying payment time to date:', error);
      return date;
    }
  };

  // Helper function to check if selected time is in the future for today's date
  const isTimeInFuture = (selectedTime: string | null): boolean => {
    if (!selectedTime) return false;
    const currentTime = getCurrentTimeInTimezone(merchant);
    return selectedTime > currentTime;
  };

  // Helper function to check if date is today
  const isDateToday = (date: Date | null): boolean => {
    if (!date) return false;
    const today = new Date();
    const selectedDate = new Date(date);
    return (
      selectedDate.getDate() === today.getDate() &&
      selectedDate.getMonth() === today.getMonth() &&
      selectedDate.getFullYear() === today.getFullYear()
    );
  };

  // Helper function to check for iframe/card errors
  const checkIframeErrors = (): string[] => {
    if (!iframeStates) return ['Card information is incomplete'];
    if (Array.isArray(iframeStates.errors) && iframeStates.errors.length > 0) {
      return iframeStates.errors;
    }
    if (iframeStates.complete === false) {
      return ['Card information is incomplete'];
    }
    return [];
  };

  // Card submission function
  const updateWithCard = () => {
    return new Promise((resolve, reject) => {
      setSendingData(true);
      const iframeErrors = checkIframeErrors();

      if (iframeErrors.length > 0) {
        setErrorMessage("Your card's information is incomplete");
        setSendingData(false);
        reject(new Error('Card information incomplete'));
        return;
      }

      setShowErrorAlert(false);
      setErrorMessage('');

      if (!form) {
        setErrorMessage('Payment form not initialized');
        setSendingData(false);
        reject(new Error('Payment form not initialized'));
        return;
      }

      try {
        form.submit(
          import.meta.env.VITE_APP_FINIX_ENVIRONMENT,
          import.meta.env.VITE_APP_FINIX_APPLICATION_ID,
          function (err: unknown, res: { data: { id: string } }) {
            if (err) {
              setErrorMessage(err instanceof Error ? err.message : String(err));
              setSendingData(false);
              reject(err);
              return;
            }

            const tokenID = res.data.id;
            setTokenID(tokenID);
            setSendingData(false);
            resolve(tokenID);
          },
        );
      } catch (err) {
        setErrorMessage('Something went wrong when submitting your request');
        setSendingData(false);
        reject(err);
      }
    });
  };

  // Fee coverage toggle handler
  const handleFeeCoverageToggle = (covered: boolean) => {
    setFeeCovered(covered);
    handleFormDataChange({ feeCovered: covered });
  };

  // Offline fee handlers
  const handleOfflineFeeCoverageToggle = (covered: boolean) => {
    setOfflineFeeCovered(covered);
    setFormData((prevData) => ({
      ...prevData,
      feeCovered: covered,
      coverTransactionFee: covered,
    }));
  };

  const handleOfflineCustomFeeAmountChange = (amount: string) => {
    setOfflineCustomFeeAmount(amount);
    setFormData((prevData) => ({
      ...prevData,
      customFeeAmount: amount,
    }));
    handleFormDataChange({ customFee: parseFloat(amount) || 0 });
  };

  // Payment date handler
  const handlePaymentDateChange = (date: Date) => {
    setPaymentDate(date);
    const timestamp = applyPaymentTimeToDate(date, paymentTime);
    setFormData((prevData) => ({
      ...prevData,
      timestamp: timestamp,
    }));
  };

  // Payment time handler
  const handlePaymentTimeChange = (time: string) => {
    setPaymentTime(time);
    const timestamp = applyPaymentTimeToDate(paymentDate, time);
    setFormData((prevData) => ({
      ...prevData,
      timestamp: timestamp,
    }));
  };

  // ============================================================================
  // End of Payment Information Section
  // ============================================================================

  // Populate possibleForms when drawer opens and API data is available
  // This effect runs when isDrawerOpen changes to true, ensuring fresh data on each drawer open
  useEffect(() => {
    if (!isDrawerOpen) return; // Don't populate when drawer is closed

    if (formData?.formToFormDataMap && Object.keys(formData.formToFormDataMap).length > 0) {
      // Convert formToFormDataMap to possibleForms array for the dropdown
      const formsArray = Object.values(formData.formToFormDataMap) as EventData[];

      if (formsArray.length > 0) {
        setPossibleForms(formsArray);
      }
    }
  }, [formData?.formToFormDataMap, isDrawerOpen]);

  // Contact search and fetch useEffects are now handled in ContactInformation component

  useEffect(() => {
    if (isEditMode && transaction?.customInputs && transaction.customInputs.length > 0) {
      // In edit mode, use transaction custom inputs
      setFormData((prevData) => ({
        ...prevData,
        globalCustomInputs: transaction
          .customInputs!.filter((input) => input.inputType === 'global')
          .map((input) => ({
            ...input,
            value: input.answers?.[0] || '', // Use the first answer from transaction
            title: input.title || input.label || '',
            options: input.options || [],
          })),
      }));
    } else if (
      !isEditMode &&
      merchant?.globalCustomInputs &&
      Array.isArray(merchant.globalCustomInputs) &&
      merchant.globalCustomInputs.length > 0
    ) {
      // In create mode, use merchant custom inputs
      const merchantInputs = merchant.globalCustomInputs as CustomInput[];
      setFormData((prevData) => ({
        ...prevData,
        globalCustomInputs: merchantInputs.map((input, index) => {
          const inputKey = input.id || input.title || input.label || `global-custom-${index}`;
          const existingInput = (prevData.globalCustomInputs ?? [])
            .filter(Boolean)
            .find(
              (currentInput: CustomInput, currentIndex: number) =>
                (currentInput.id ||
                  currentInput.title ||
                  currentInput.label ||
                  `global-custom-${currentIndex}`) === inputKey,
            );

          return {
            ...input,
            value: existingInput?.value ?? input.value ?? '', // Preserve typed values if this effect reruns
            title: input.title || input.label || '',
            options: input.options || [],
          };
        }),
      }));
    }
  }, [merchant?.globalCustomInputs, transaction?.customInputs, isEditMode]);

  // Payment-related helper functions are now handled by PaymentInformation component

  // Add error display component at the top of the form
  const ErrorDisplay = () => {
    if (!paymentError?.show) return null;

    return (
      <div className="mb-4 rounded-md border border-red-200  p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">
              Please correct the following errors:
            </h3>
            <div className="mt-2 text-sm text-[#A04040]">
              <ul className="list-disc space-y-1 pl-5">
                {Array.isArray(paymentError.message) ? (
                  paymentError.message.map((msg, idx) => <li key={idx}>{msg}</li>)
                ) : (
                  <li>{paymentError.message}</li>
                )}
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Reset EventLayout's internal state when drawer closes
  // Note: formData is reset by parent component's clearStates() - we only reset local state here
  // Payment-related state resets are now handled by PaymentInformation component
  useEffect(() => {
    if (!isDrawerOpen) {
      setPossibleForms([]);

      // Reset contact selection state (passed from parent)
      setSelectedContact(null);
      setIsNewContact(false);

      // Reset contact search state (lifted from ContactInformation)
      setContactSearchTerm('');
      setContactSearchResults([]);
      setContactHasSearched(false);
      setContactSelectedEmailIndex(0);
      setContactSelectedPhoneIndex(0);
      setContactSelectedAddressIndex(0);
      setContactIsAddingNewEmail(false);
      setContactIsAddingNewPhone(false);
      setContactIsAddingNewAddress(false);

      // Reset parent component states
      handleFormDataChange({ feeCovered: true });
    }
  }, [isDrawerOpen]);

  // Edit mode payment type initialization is now handled by PaymentInformation component

  // Initialize selectedTickets from formData.tickets in edit mode
  useEffect(() => {
    if (isEditMode && formData.tickets && formData.tickets.length > 0) {
      // Check if tickets have actual data (not just empty placeholders)
      const hasRealTicketData = formData.tickets.some(
        (ticket) => ticket.ticketType && ticket.ticketID,
      );

      if (hasRealTicketData && selectedTickets.length === 0) {
        // Convert formData.tickets to selectedTickets format for UI
        const convertedSelectedTickets: SelectedTicket[] = formData.tickets.map(
          (ticket, index) => ({
            ticketIndex: index,
            ticketType: ticket.ticketType || '',
            ticketID: ticket.ticketID || '',
            ticketPrice: ticket.ticketPrice || 0,
            seats: ticket.seats || 1,
            customInputs: [],
          }),
        );

        setSelectedTickets(convertedSelectedTickets);
      }
    }
  }, [isEditMode, formData.tickets, selectedTickets.length]);

  // Set selectedEventData when possibleForms becomes available in edit mode
  useEffect(() => {
    if (
      isEditMode &&
      possibleForms.length > 0 &&
      (formData.selectedEvent || formData.formName) &&
      !formData.selectedEventData
    ) {
      // Find the event in possibleForms (filter for EVENT type)
      const eventName = formData.selectedEvent || formData.formName;
      const selectedEvent = possibleForms.find(
        (f) => f.formName === eventName && f.formType === 'EVENT',
      );
      if (selectedEvent) {
        setFormData((prev) => ({
          ...prev,
          selectedEvent: eventName,
          selectedEventData: selectedEvent as unknown as EventData,
        }));
      }
    }
  }, [
    isEditMode,
    possibleForms,
    formData.selectedEvent,
    formData.formName,
    formData.selectedEventData,
  ]);

  // Contact-related handlers are now in ContactInformation component
  // Payment-related state management is now in PaymentInformation component

  // Reset form states helper - only handles non-payment states now
  const resetFormStates = useCallback(() => {
    // Reset contact selection state (passed from parent)
    setSelectedContact(null);
    setIsNewContact(false);
    // Reset bank account states via formData
    handleFormDataChange({
      accountNumber: '',
      routingNumber: '',
      accountType: 'CHECKING',
    });
  }, [setSelectedContact, setIsNewContact, handleFormDataChange]);

  // Reset form states when transaction changes (for edit mode)
  useEffect(() => {
    if (isEditMode && transaction) {
      resetFormStates();
    }
  }, [transaction?.transactionID, isEditMode, resetFormStates]);

  // Payment time validation is now handled by PaymentInformation component

  // Calculate if all selected tickets have 0 price (free tickets)
  // This is used to hide payment type selector and show simplified payment info
  const isFreeTickets = useMemo(() => {
    // Check if there are any tickets with attendees
    const hasTicketsWithAttendees = formData.tickets?.some(
      (ticket) => ticket.ticketType && ticket.attendees?.length > 0,
    );

    if (!hasTicketsWithAttendees) return false;

    // Calculate total ticket price (same logic as in CreateEventPaymentDrawer)
    const ticketsTotal =
      formData.tickets?.reduce((total, ticket) => {
        return total + calculateDashboardTicketRowSubtotal(ticket, formData.selectedEventData);
      }, 0) || 0;

    // Apply promo discount to determine if this is a free ticket transaction
    const amountAfterDiscount = Math.max(0, ticketsTotal - (formData.totalPromoDiscount || 0));

    return amountAfterDiscount === 0;
  }, [formData.tickets, formData.totalPromoDiscount]);

  // Update payment type to 'Free' when tickets are free, revert when not
  useEffect(() => {
    if (isFreeTickets) {
      handleFormDataChange({
        paymentType: 'Free',
        feeCovered: false,
      });
    } else if (formData.paymentType === 'Free') {
      // Revert to Cash in edit mode, Online in create mode when tickets are no longer free
      const newPaymentType = isEditMode ? 'Cash' : 'Online';
      handleFormDataChange({
        paymentType: newPaymentType,
        feeCovered: isEditMode
          ? props.transaction?.coverFees === 'Yes' || props.transaction?.coverFees === true
          : true,
      });
    }
  }, [isFreeTickets, isEditMode]);

  // Helper function to check if a tab/step has validation errors
  const stepHasErrors = useCallback(
    (step: FormStep): boolean => {
      if (!invalidFields || invalidFields.size === 0) return false;
      return Array.from(invalidFields).some((field) => field.startsWith(step + '-'));
    },
    [invalidFields],
  );

  return (
    <div className="space-y-4">
      {/* Add error display at the top */}
      <ErrorDisplay />

      <PaymentEditorSection
        presentation={presentation}
        title="Event and tickets"
        description="Choose the event, tickets, attendees, and discounts."
        icon={TicketIcon}
        hasErrors={stepHasErrors('form_information')}
        active={currentStep === 'form_information'}
        onClick={() => setCurrentStep('form_information')}
      >
        <div>
          <EventInformation
            formData={formData}
            setFormData={setFormData}
            handleFormDataChange={handleFormDataChange}
            merchant={merchant}
            invalidFields={invalidFields}
            handleFieldFocus={handleFieldFocus}
            isEditMode={isEditMode}
            paymentType={paymentType || 'Online'}
            isDrawerOpen={isDrawerOpen}
            transaction={transaction}
            validatePromoCode={validatePromoCode}
            removePromoCode={removePromoCode}
            isPromoProcessing={isPromoProcessing}
            promoMessage={promoMessage}
          />

          {/* Navigation buttons */}
          {presentation !== 'workspace' && (
            <StepNavigation
              currentStep={currentStep}
              onBack={handleBack}
              onContinue={handleContinue}
              paymentType={paymentType}
              isEditMode={isEditMode}
            />
          )}
        </div>
      </PaymentEditorSection>

      {/* Only show Custom Inputs Information section if there are global or purchaser custom inputs */}
      {((isEditMode && formData.globalCustomInputs && formData.globalCustomInputs.length > 0) ||
        (!isEditMode && merchant?.globalCustomInputs && merchant.globalCustomInputs.length > 0) ||
        (formData.purchaserCustomInputs && formData.purchaserCustomInputs.length > 0)) && (
        <PaymentEditorSection
          presentation={presentation}
          title="Custom Input Responses"
          description="Capture merchant, event, and purchaser responses."
          icon={ChatBubbleLeftRightIcon}
          hasErrors={stepHasErrors('custom_input_information')}
          active={currentStep === 'custom_input_information'}
          onClick={() => setCurrentStep('custom_input_information')}
        >
          <div>
            <CustomInputsInformation_Event
              formData={formData}
              setFormData={setFormData}
              merchant={merchant}
              isEditMode={isEditMode}
              handleFieldFocus={handleFieldFocus}
              invalidFields={invalidFields}
            />

            {/* Navigation buttons */}
            {presentation !== 'workspace' && (
              <StepNavigation
                currentStep={currentStep}
                onBack={handleBack}
                onContinue={handleContinue}
                paymentType={paymentType}
                isEditMode={isEditMode}
              />
            )}
          </div>
        </PaymentEditorSection>
      )}

      <PaymentEditorSection
        presentation={presentation}
        title="Contact Information"
        description="Select an existing contact or enter a new attendee purchaser."
        icon={UserIcon}
        hasErrors={stepHasErrors('contact_information')}
        active={currentStep === 'contact_information'}
        onClick={() => setCurrentStep('contact_information')}
      >
        <div>
          <ContactInformation
            formData={formData}
            setFormData={setFormData}
            merchant={merchant}
            invalidFields={invalidFields}
            setInvalidFields={setInvalidFields}
            handleFieldFocus={handleFieldFocus}
            isDrawerOpen={isDrawerOpen}
            isEditMode={isEditMode}
            transaction={transaction}
            selectedContact={selectedContact}
            setSelectedContact={setSelectedContact}
            isNewContact={isNewContact}
            setIsNewContact={setIsNewContact}
            contactSearchState={contactSearchState}
          />

          {/* Navigation buttons */}
          {presentation !== 'workspace' && (
            <StepNavigation
              currentStep={currentStep}
              onBack={handleBack}
              onContinue={handleContinue}
              paymentType={paymentType}
              isEditMode={isEditMode}
              selectedContact={selectedContact}
              isNewContact={isNewContact}
            />
          )}
        </div>
      </PaymentEditorSection>

      <PaymentEditorSection
        presentation={presentation}
        title="Payment Method"
        description="Choose the payment method and fee preference."
        icon={CreditCardIcon}
        hasErrors={stepHasErrors('payment_information')}
        active={currentStep === 'payment_information'}
        onClick={() => setCurrentStep('payment_information')}
        keepMounted
      >
        {/* Always render PaymentInformation to allow card iframe preloading, but hide when not on this step */}
        <div>
          <PaymentInformation
            presentation={presentation}
            formData={formData}
            setFormData={setFormData}
            merchant={merchant}
            feeCovered={feeCovered}
            handleFeeCoverageToggle={handleFeeCoverageToggle}
            invalidFields={invalidFields}
            handleFieldFocus={handleFieldFocus}
            paymentMethod={paymentMethod}
            setPaymentMethod={setPaymentMethod}
            stripeCardRef={stripeCardRef}
            finixCardRef={finixCardRef}
            finixBankRef={finixBankRef}
            form={form}
            isDrawerOpen={isDrawerOpen}
            isEditMode={isEditMode}
            paymentType={paymentType || 'Online'}
            stripePromise={stripePromise}
            iframeStates={iframeStates}
            updateWithCard={updateWithCard}
            accountNumber={accountNumber}
            setAccountNumber={setAccountNumber}
            routingNumber={routingNumber}
            setRoutingNumber={setRoutingNumber}
            accountType={accountType}
            setAccountType={setAccountType}
            offlineFeeCovered={offlineFeeCovered}
            handleOfflineFeeCoverageToggle={handleOfflineFeeCoverageToggle}
            offlineCustomFeeAmount={offlineCustomFeeAmount}
            handleOfflineCustomFeeAmountChange={handleOfflineCustomFeeAmountChange}
            paymentDate={paymentDate}
            handlePaymentDateChange={handlePaymentDateChange}
            paymentTime={paymentTime}
            handlePaymentTimeChange={handlePaymentTimeChange}
            isDateToday={isDateToday}
            isTimeInFuture={isTimeInFuture}
            setShowErrorAlert={setShowErrorAlert}
            setSendingData={setSendingData}
          />
          {presentation !== 'workspace' && currentStep === 'payment_information' && (
            <StepNavigation
              currentStep={currentStep}
              onBack={handleBack}
              onContinue={handleContinue}
              paymentType={paymentType}
              isEditMode={isEditMode}
            />
          )}
        </div>
      </PaymentEditorSection>
    </div>
  );
}
